import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DeviceInfo } from "../device/device-info";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from "@ngrx/store";
import { SessionState } from "./session.reducer";
import { SessionService } from "./session.service";
import * as SessionActions from './session.actions';
import { environment } from '../../environments/environment';
import { of } from 'rxjs';
import {
  catchError,
  map,
  concatMap,
  tap,
  map as rxMap,
  withLatestFrom,
  filter,
} from 'rxjs/operators';
import { IApiUserSession } from "../interfaces/user.api.interface";
import { CookieService } from "../api/cookie.api.service";

@Injectable()
export class SessionEffects{

    constructor(
        private actions$: Actions,
        private http: HttpClient,
        private router: Router,
        private state: Store<{ session: SessionState }>,
        private session: SessionService,
        private cookieService: CookieService
      ) {
      }

      storeSession$ = createEffect(() => {
        return this.actions$.pipe(
          ofType(SessionActions.storeSession),
          concatMap(({ session }) => {
            try {
              if (session) {
                localStorage.setItem('session', btoa(JSON.stringify(session)));
              } else {
                // Se limpia el localStorage conservando estas claves y el identificador del móvil
                DeviceInfo.clearStorage(['darkMode', 'selectedCenter', 'selectedShift']);

              }
              return of(SessionActions.storeSessionSuccess());
            } catch (error) {
              console.error(error);
              return of(SessionActions.storeSessionFailure({ error }));
            }
          })
        );
      });

    login$ = createEffect(() => {      
        return this.actions$.pipe(
          ofType(SessionActions.login),
          concatMap(({ credentials }) => 
            this.http.post<IApiUserSession>(`${environment.apiBaseUrl}/session/signInGoogle`, credentials, {withCredentials: true}).pipe(
              map(session => 
                SessionActions.loginSuccess({ session }),
              ),                            
              catchError(error => 
                of(SessionActions.loginFailure({ error })))
            )
          )
        );
      });

      
//{error: true, errorMessage : null} as ILoginError
      loginSuccess$ = createEffect(() => {
        return this.actions$.pipe(
          ofType(SessionActions.loginSuccess),
          tap(),
          map(({ session }) => {
            //Navegar a home
            this.router.navigate(['/home']);
            return SessionActions.storeSession({ session });
          })
        );
      });


      loginFailures$ = createEffect(() => {
        return this.actions$.pipe(
          ofType(SessionActions.loginFailure),
          tap(),
          map(({ error }) => {
            return SessionActions.storeSession({ session:null });
          })
        );
      });  


      logout$ = createEffect(() => {
        return this.actions$.pipe(
          ofType(SessionActions.logout),
          concatMap(() => 
            this.http.delete(`${environment.apiBaseUrl}/session/?isApp=${environment.isAPP}`, {withCredentials: true}).pipe(
              map(() => SessionActions.logoutSuccess()),                            
              catchError(error => {
                // Incluso si el backend falla, forzamos la limpieza del cliente y tratamos como éxito local
                console.error('Logout failed on backend, but forcing client cleanup.', error);
                return of(SessionActions.logoutSuccess()); // Tratar como éxito para que se ejecute la limpieza
              })
            )
          )       
        );
      });
    
      logoutSuccess$ = createEffect(() => {
        return this.actions$.pipe(
          ofType(SessionActions.logoutSuccess),
          tap(() => {
            console.log('Logout successful on backend or forced. Cleaning client state...');
            
            // 1. Limpieza exhaustiva de cookies
            this.cookieService.removeAllCookies();

            // 2. Limpieza de todo el almacenamiento local y de sesión (se conserva el identificador del móvil)
            DeviceInfo.clearStorage(['darkMode']);
            sessionStorage.clear();

            // 3. Forzar recarga de la página para un estado 100% limpio
            // Esto es crucial para eliminar cualquier estado en memoria de Angular.
            // Usamos replace para evitar que el usuario vuelva atrás y un timestamp para evitar la caché.
            window.location.replace('/login?reload=' + new Date().getTime());
          }),
          // Este map es necesario para que el effect se complete, pero la acción no será procesada
          // por ningún reducer porque la página se habrá recargado.
          map(() => SessionActions.storeSession({ session: null }))
        );
      });

      logoutFailure$ = createEffect(() => {
        return this.actions$.pipe(
          ofType(SessionActions.logoutFailure),
          tap((err) => console.error('This should not be reached if logoutSuccess is forced', err)) 
        );
      });

      storeUserPermissions$ = createEffect(() => {      
        return this.actions$.pipe(
          ofType(SessionActions.storeUserPermissions),
          map(({ permissions }) => 
            {
              //Actualizamos los permisos
              const session = JSON.parse(atob(localStorage.getItem('session'))) as IApiUserSession;
              session.ListPermissions = permissions;
              return SessionActions.storeSession({ session }); 
            }
          )
        );
      });
      
}